import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Checkbox } from '@heyroot/ui/primitives/Checkbox';
import { Input } from '@heyroot/ui/primitives/Input';
import {
  PERMISSION_CATALOG,
  PERMISSION_GROUPS,
  type PermissionMeta,
} from '../../../rbac/permission-catalog';
import type { PermName } from '../../../rbac/permissions';

export interface PermissionBitPickerProps {
  selected: string[];
  onChange: (next: string[]) => void;
  readOnly?: boolean;
}

function matches(entry: PermissionMeta, needle: string): boolean {
  const n = needle.toLowerCase();
  return (
    entry.label.toLowerCase().includes(n) ||
    entry.name.toLowerCase().includes(n) ||
    entry.description.toLowerCase().includes(n)
  );
}

export function PermissionBitPicker({
  selected,
  onChange,
  readOnly = false,
}: PermissionBitPickerProps) {
  const [query, setQuery] = useState('');
  // Fix round 2 finding 4: the brief requires collapsible groups twice --
  // forty permissions across ten always-open sections is the wall of
  // checkboxes this picker exists to avoid. Default open per group.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(PERMISSION_GROUPS.map((g) => [g, true])),
  );

  function toggleGroup(group: string) {
    setOpenGroups((prev) => ({ ...prev, [group]: !(prev[group] ?? true) }));
  }

  const byGroup = useMemo(() => {
    const q = query.trim();
    const filtered = q ? PERMISSION_CATALOG.filter((e) => matches(e, q)) : PERMISSION_CATALOG;
    const map = new Map<string, PermissionMeta[]>();
    for (const group of PERMISSION_GROUPS) map.set(group, []);
    for (const entry of filtered) {
      const list = map.get(entry.group) ?? [];
      list.push(entry);
      map.set(entry.group, list);
    }
    return map;
  }, [query]);

  const selectedSet = new Set(selected);

  function toggle(name: PermName) {
    if (readOnly) return;
    if (selectedSet.has(name)) {
      onChange(selected.filter((n) => n !== name));
    } else {
      onChange([...selected, name]);
    }
  }

  function selectAll(group: string, entries: PermissionMeta[]) {
    const names = entries.map((e) => e.name);
    const next = new Set(selected);
    for (const n of names) next.add(n);
    onChange(Array.from(next));
  }

  function clearGroup(entries: PermissionMeta[]) {
    const names = new Set(entries.map((e) => e.name));
    onChange(selected.filter((n) => !names.has(n as PermName)));
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search permissions"
          aria-label="Search permissions"
          className="max-w-xs"
        />
        <Badge variant="neutral">{selected.length} selected</Badge>
      </div>

      {PERMISSION_GROUPS.map((group) => {
        const entries = byGroup.get(group) ?? [];
        if (query.trim() && entries.length === 0) return null;
        // A search in progress always shows its matches, regardless of the
        // group's own collapse state -- collapsing exists to reduce the
        // at-rest wall of checkboxes, not to hide search results.
        const isOpen = query.trim() ? true : (openGroups[group] ?? true);
        return (
          <section key={group} className="rounded-control border border-border bg-surface p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              {/* Disclosure toggle, not a control-weight action: variant="link" is
                  sizing-agnostic, so it carries no button chrome or height. */}
              <Button
                variant="link"
                className="gap-1.5 text-body-sm text-primary no-underline hover:no-underline"
                aria-expanded={isOpen}
                onClick={() => toggleGroup(group)}
              >
                {isOpen ? (
                  <ChevronDown size={14} className="text-muted" aria-hidden="true" />
                ) : (
                  <ChevronRight size={14} className="text-muted" aria-hidden="true" />
                )}
                {group}
              </Button>
              {!readOnly && (
                <div className="flex gap-2">
                  <Button
                    variant="link"
                    aria-label={`Select all ${group}`}
                    className="text-caption text-secondary hover:text-primary"
                    onClick={() => selectAll(group, entries)}
                  >
                    Select all
                  </Button>
                  <Button
                    variant="link"
                    aria-label={`Clear ${group}`}
                    className="text-caption text-secondary hover:text-primary"
                    onClick={() => clearGroup(entries)}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
            {isOpen && (
              <ul className="flex flex-col gap-1.5">
                {entries.map((entry) => (
                  <li key={entry.name} className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedSet.has(entry.name)}
                      disabled={readOnly}
                      aria-label={entry.label}
                      onChange={() => toggle(entry.name)}
                    />
                    <span className="text-body-sm text-primary">{entry.label}</span>
                    <span className="text-caption text-muted">{entry.description}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
